/**
 * /app/$tenantSlug/shifts — My Shifts.
 *
 * The employee's upcoming roster (next 14 days) + a CTA to request a swap.
 * Roster scope is server-enforced (self).
 */

import { useMemo } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { TimeAndLeaveShell } from "@/components/shared/time-and-leave-shell";
import { useLeaveRoles } from "@/components/leave/use-leave-roles";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/shared/empty-state";
import { useRoster, useShifts } from "@/hooks/use-shifts";
import { formatDate } from "@/lib/format";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/shifts/")({
  component: MyShiftsRoute,
});

function fmtMinute(m: number): string {
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return `${String(h).padStart(2, "0")}:${String(mm).padStart(2, "0")}`;
}

function MyShiftsRoute() {
  const params = Route.useParams();
  const { isApprover } = useLeaveRoles();
  const range = useMemo(() => {
    const from = new Date();
    const to = new Date();
    to.setDate(to.getDate() + 13);
    return { from: from.toISOString().slice(0, 10), to: to.toISOString().slice(0, 10) };
  }, []);
  const roster = useRoster(range);
  const shifts = useShifts();

  const shiftById = useMemo(() => {
    const m = new Map<number, { name: string; start: number; end: number }>();
    for (const s of shifts.data?.shifts ?? []) {
      m.set(s.id, { name: s.name, start: s.start_minute, end: s.end_minute });
    }
    return m;
  }, [shifts.data]);

  return (
    <TimeAndLeaveShell
      title="Shifts"
      description="Your upcoming roster."
      actions={
        <div className="flex flex-wrap gap-2">
          <Button asChild variant="outline">
            <Link to="/app/$tenantSlug/shifts/swaps" params={params}>
              Request a Swap
            </Link>
          </Button>
          {isApprover && (
            <Button asChild variant="outline">
              <Link to="/app/$tenantSlug/shifts/roster" params={params}>
                Roster
              </Link>
            </Button>
          )}
        </div>
      }
    >
      <Card>
        <CardHeader>
          <CardTitle>Next 14 Days</CardTitle>
        </CardHeader>
        <CardContent>
          {roster.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
            </div>
          ) : roster.data && roster.data.roster.length > 0 ? (
            <ul className="divide-y text-sm">
              {roster.data.roster.map((ra) => {
                const s = shiftById.get(ra.shift_id);
                return (
                  <li key={ra.id} className="flex items-center justify-between py-2">
                    <span>{formatDate(ra.work_date)}</span>
                    <span className="flex items-center gap-2">
                      <span>{s ? s.name : `Shift #${ra.shift_id}`}</span>
                      {s && (
                        <span className="text-muted-foreground">
                          {fmtMinute(s.start)}–{fmtMinute(s.end)}
                        </span>
                      )}
                      {ra.assigned_via === "swap" && (
                        <Badge variant="secondary">swapped</Badge>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <EmptyState title="No upcoming shifts rostered." />
          )}
        </CardContent>
      </Card>
    </TimeAndLeaveShell>
  );
}
